'use client'

import { ReactNode, useState } from 'react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import { PortfolioModal } from '@/components/PortfolioModal'
import { PortfolioShowcase } from '@/components/PortfolioShowcase'
import { ExternalLink, Sparkles } from 'lucide-react'
import { EnhancedProgress } from './EnhancedProgress'
import { SocialProofBanner } from '../chat/SocialProofBanner'
import { ProjectSummary } from '../chat/ProjectSummary'
import { CosmicShader } from '@/components/ui/cosmic-shader'
import type { FormData } from '@/hooks/useAppStore'

// Map string step to numeric step for EnhancedProgress
const STEP_MAP: Record<string, number> = {
  intro: 0, name: 1, contact: 2, profile: 3, situation: 4,
  objective: 5, features: 6, branding: 7, content: 8,
  urgency: 9, details: 10, project_type: 10, lgpd: 11,
  submitting: 12, success: 13
}

interface QuizLayoutProps {
  children: ReactNode
  currentStep: string
  formData: FormData
}

export function QuizLayout({ children, currentStep, formData }: QuizLayoutProps) {
  const [showPortfolio, setShowPortfolio] = useState(false)

  return (
    <div className="h-[100dvh] w-full flex overflow-hidden relative">
      {/* Background animado com WebGL */}
      <CosmicShader />

      {/* Main content area */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row w-full">
        {/* Coluna esquerda - Quiz Card centralizado com scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="min-h-full flex flex-col">
            {/* Header */}
            <div className="shrink-0 px-3 py-2 lg:p-6 flex flex-col gap-2 lg:gap-3">
              <div className="flex items-center justify-between">
                {/* Logo - mesmo tamanho e estilo da Hero */}
                <a href="/">
                  {/* Light mode logo */}
                  <Image
                    src="/logo-biomo-mini-light.svg"
                    alt="Biomo"
                    width={44}
                    height={56}
                    sizes="44px"
                    className="h-10 sm:h-11 w-auto dark:hidden"
                  />
                  {/* Dark mode logo */}
                  <Image
                    src="/logo-biomo-mini.svg"
                    alt="Biomo"
                    width={44}
                    height={56}
                    sizes="44px"
                    className="h-10 sm:h-11 w-auto hidden dark:block"
                  />
                </a>

                <button
                  onClick={() => setShowPortfolio(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 dark:bg-white/5 backdrop-blur-xl hover:bg-white/90 dark:hover:bg-white/8 border border-purple-500/20 dark:border-white/10 text-indigo-950/70 dark:text-white/70 hover:text-indigo-950 dark:hover:text-white transition-all text-sm"
                >
                  <Sparkles size={14} className="text-purple-500 dark:text-purple-400" />
                  <span>Portfolio</span>
                </button>
              </div>

              {/* Enhanced Progress Bar com fases visuais */}
              <EnhancedProgress currentStep={STEP_MAP[currentStep] || 0} />
            </div>

            {/* Social Proof - Mobile Only */}
            <div className="lg:hidden shrink-0 px-3 pb-1">
              <SocialProofBanner variant="compact" />
            </div>

            {/* Quiz Card Area - Centralizado verticalmente */}
            <div className="relative flex-1 flex items-center justify-center px-3 py-3 sm:py-8">
              {children}
            </div>
          </div>
        </div>

        {/* Coluna direita - Sidebar (apenas desktop) */}
        <div className="hidden lg:flex w-[400px] xl:w-[480px] h-screen flex-col relative overflow-hidden border-l border-purple-500/20 dark:border-white/10">
          <div className="w-full h-full flex flex-col py-4 px-4 gap-4">
            {/* Portfolio Showcase */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="shrink-0"
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles size={14} className="text-purple-500 dark:text-purple-400" />
                <h3 className="text-indigo-950/80 dark:text-white/80 text-sm font-medium">Nossos Projetos</h3>
              </div>
              <PortfolioShowcase variant="compact" autoPlayInterval={5000} />
            </motion.div>

            {/* Stats compactos */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="shrink-0"
            >
              <SocialProofBanner variant="compact" />
            </motion.div>

            {/* Project Summary - cresce para preencher */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex-1 min-h-0 rounded-2xl bg-white/85 dark:bg-white/5 backdrop-blur-xl border border-purple-500/20 dark:border-white/10 overflow-hidden flex flex-col"
            >
              <ProjectSummary formData={formData} currentStep={currentStep} />
            </motion.div>

            {/* CTA ver portfolio */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="shrink-0 text-center"
            >
              <button
                onClick={() => setShowPortfolio(true)}
                className="inline-flex items-center gap-2 text-purple-500 dark:text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 text-sm font-medium transition-colors group"
              >
                <span>Ver todos os projetos</span>
                <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </motion.div>
          </div>

        </div>
      </div>

      {/* Portfolio Modal */}
      <PortfolioModal isOpen={showPortfolio} onClose={() => setShowPortfolio(false)} />
    </div>
  )
}
